import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * SSO Callback Page — receives JWT from SAML redirect.
 * URL: /auth/sso-callback?token=<jwt>
 *
 * Flow:
 * 1. IdP validates user → POST to /api/auth/saml/callback
 * 2. Server validates SAML → generates JWT → redirects here with token
 * 3. This page stores the JWT and enters the app
 */
function SsoCallback() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const errorMsg = params.get('error');
    const embedded = params.get('embedded') === '1';
    const patientId = params.get('patient_id');
    const deptId = params.get('dept_id');

    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      return;
    }

    if (token) {
      try {
        loginWithToken(token);
        // Persist embedded flag for the session so Sidebar/PlatformHeader
        // know to hide chrome on every page, not just this callback.
        if (embedded) sessionStorage.setItem('nh_embedded', '1');
        if (deptId) sessionStorage.setItem('nh_embedded_dept_id', deptId);

        // If the SAML launch carried patient context (Apps Tab / Encounter
        // Card launch), deep-link the user there instead of the dashboard.
        const target = patientId ? `/patients/${encodeURIComponent(patientId)}` : '/';
        navigate(target, { replace: true });
      } catch (err) {
        setError('Failed to complete SSO login. Please try again.');
      }
    } else {
      setError('No authentication token received. Please try again.');
    }
  }, [loginWithToken, navigate]);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <h2 style={styles.title}>SSO Login Failed</h2>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.button} onClick={() => navigate('/login', { replace: true })}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.spinner} />
        <p style={styles.text}>Completing sign-in...</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#f8fafc',
  },
  card: {
    background: '#fff', borderRadius: 12, padding: '40px 48px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: 400,
  },
  title: { fontSize: 20, fontWeight: 600, color: '#1e293b', margin: '16px 0 8px' },
  text: { fontSize: 14, color: '#64748b' },
  errorIcon: {
    width: 48, height: 48, borderRadius: '50%', background: '#fef2f2',
    color: '#dc2626', fontSize: 24, fontWeight: 700, lineHeight: '48px',
    margin: '0 auto',
  },
  errorText: { fontSize: 14, color: '#dc2626', margin: '8px 0 24px' },
  button: {
    padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
  },
  spinner: {
    width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#2563eb',
    borderRadius: '50%', margin: '0 auto 16px',
    animation: 'spin 0.8s linear infinite',
  },
};

export default SsoCallback;
